<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { scheduleApi, eventsApi, type ScheduleItem, type ScheduleResponse } from '@/api'
import { getColour } from '@/constants/colors'
import { toLocalDateKey } from '@/utils/date'
import { useScheduleSettingsStore } from '@/store/ScheduleSettingsStore'
import { useAuthStore } from '@/store/AuthStore'
import { AppCard, ScheduleItem as ScheduleItemView, fromScheduleItem } from '@/design-system/components'
import WeekStrip from '@/components/schedule/WeekStrip.vue'
import type { ScheduleCardItem } from '@/components/schedule/ScheduleItemCard.vue'
import CreateLessonModal from '@/components/modals/CreateLessonModal.vue'
import ScheduleLessonsModal from '@/components/modals/ScheduleLessonsModal.vue'
import CreateEventModal from '@/components/modals/CreateEventModal.vue'

const router = useRouter()
const scheduleSettings = useScheduleSettingsStore()
const auth = useAuthStore()

// ── View mode ──
type ViewMode = 'week' | 'month'
const viewMode = ref<ViewMode>('week')

// ── State ──
const currentDate = ref(new Date())
const selectedDate = ref(new Date())
const loading = ref(false)
const settingsLoading = ref(true)
const scheduleData = ref<ScheduleResponse | null>(null)

// ── Color helper ──
function getColorStyle(colour?: string) {
  const color = getColour(colour)
  return { hex: color.hex }
}

function isSameDay(d1: Date, d2: Date): boolean {
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate()
}

// ── Week calculations (uses schedule settings) ──
const weekStart = computed(() => scheduleSettings.getWeekStart(currentDate.value))

const schoolDayOffsets = computed(() => {
  // Get JS day numbers for school days, compute their offsets from weekStart
  const startJs = weekStart.value.getDay()
  return scheduleSettings.schoolDayJsNumbers.map(jsDay => (jsDay - startJs + 7) % 7)
})

const weekEnd = computed(() => {
  const offsets = schoolDayOffsets.value
  const lastOffset = offsets.length > 0 ? offsets[offsets.length - 1]! : 4
  const date = new Date(weekStart.value)
  date.setDate(date.getDate() + lastOffset)
  return date
})

const weekDays = computed(() => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return schoolDayOffsets.value.map(offset => {
    const date = new Date(weekStart.value)
    date.setDate(weekStart.value.getDate() + offset)
    return {
      date,
      dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
      dayNumber: date.getDate(),
      dateString: toLocalDateKey(date),
      isToday: isSameDay(date, new Date()),
      isPast: date < today && !isSameDay(date, new Date()),
    }
  })
})

const weekLabel = computed(() => {
  const s = weekStart.value
  const e = weekEnd.value
  const sm = s.toLocaleDateString('en-US', { month: 'short' })
  const em = e.toLocaleDateString('en-US', { month: 'short' })
  const y = e.getFullYear()
  return sm === em ? `${sm} ${s.getDate()} - ${e.getDate()}, ${y}` : `${sm} ${s.getDate()} - ${em} ${e.getDate()}, ${y}`
})

// ── Month calculations ──
const monthStart = computed(() => {
  const date = new Date(currentDate.value)
  date.setDate(1)
  date.setHours(0, 0, 0, 0)
  return date
})

const monthEnd = computed(() => {
  const date = new Date(currentDate.value)
  date.setMonth(date.getMonth() + 1)
  date.setDate(0)
  return date
})

const monthLabel = computed(() =>
  currentDate.value.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
)

const calendarDays = computed(() => {
  const days: Array<{ date: Date; dateString: string; isCurrentMonth: boolean; isToday: boolean; isSelected: boolean; isSchoolDay: boolean }> = []
  const firstDay = scheduleSettings.getCalendarStart(monthStart.value)
  const schoolJsDays = new Set(scheduleSettings.schoolDayJsNumbers)
  for (let i = 0; i < 42; i++) {
    const date = new Date(firstDay)
    date.setDate(firstDay.getDate() + i)
    days.push({
      date,
      dateString: toLocalDateKey(date),
      isCurrentMonth: date.getMonth() === currentDate.value.getMonth(),
      isToday: isSameDay(date, new Date()),
      isSelected: isSameDay(date, selectedDate.value),
      isSchoolDay: schoolJsDays.has(date.getDay()),
    })
  }
  return days
})

// ── Items by date (from backend schedule response, includes events) ──
const itemsByDate = computed(() => {
  const map = new Map<string, ScheduleItem[]>()
  scheduleData.value?.items.forEach(item => {
    const date = item.dateTime.split('T')[0] ?? ''
    if (!date) return
    if (!map.has(date)) map.set(date, [])
    map.get(date)!.push(item)
  })

  map.forEach(items => items.sort((a, b) => a.dateTime.localeCompare(b.dateTime)))
  return map
})

const selectedDayItems = computed(() => itemsByDate.value.get(toLocalDateKey(selectedDate.value)) || [])

// ── Navigation ──
function previousPeriod() {
  const date = new Date(currentDate.value)
  if (viewMode.value === 'week') date.setDate(date.getDate() - 7)
  else date.setMonth(date.getMonth() - 1)
  currentDate.value = date
}

function nextPeriod() {
  const date = new Date(currentDate.value)
  if (viewMode.value === 'week') date.setDate(date.getDate() + 7)
  else date.setMonth(date.getMonth() + 1)
  currentDate.value = date
}

function goToToday() {
  currentDate.value = new Date()
  selectedDate.value = new Date()
}

function selectDay(date: Date) {
  selectedDate.value = date
}

function navigateToItem(item: ScheduleCardItem) {
  if (!auth.isTeacher || !item.classId) return
  if (item.type === 'lesson') router.push(`/classes/${item.classId}/lesson/${item.id}`)
  else if (item.type === 'assignment') router.push(`/classes/${item.classId}/assignment/${item.id}`)
}

// ── Data fetching ──
async function fetchSchedule() {
  loading.value = true
  try {
    let from: string, to: string
    if (viewMode.value === 'week') {
      from = toLocalDateKey(weekStart.value)
      to = toLocalDateKey(new Date(weekStart.value.getTime() + 6 * 86400000))
    } else {
      const start = new Date(monthStart.value)
      start.setDate(start.getDate() - 7)
      const end = new Date(monthEnd.value)
      end.setDate(end.getDate() + 7)
      from = toLocalDateKey(start)
      to = toLocalDateKey(end)
    }
    scheduleData.value = await scheduleApi.getSchedule({ from, to })
  } catch (err) {
    console.error('Failed to load schedule:', err)
  } finally {
    loading.value = false
  }
}

// ── Add flow ──
const showCreateLesson = ref(false)
const showScheduleLessons = ref(false)
const showCreateEvent = ref(false)
const addDropdownRef = ref<HTMLElement | null>(null)
const showAddDropdown = ref(false)

function openAddSingle(date?: Date) {
  showAddDropdown.value = false
  if (date) selectedDate.value = date
  showCreateLesson.value = true
}

function openAddSchedule() {
  showAddDropdown.value = false
  showScheduleLessons.value = true
}

function openAddEvent() {
  showAddDropdown.value = false
  showCreateEvent.value = true
}

async function onEventCreated(event: {
  title: string
  startDateTime: string
  endDateTime?: string
  allDay?: boolean
  location?: string
  description?: string
  classId?: string
  className?: string
  classColour?: string
}) {
  try {
    await eventsApi.createEvent({
      title: event.title,
      startTime: event.startDateTime,
      endTime: event.endDateTime,
      allDay: event.allDay,
      location: event.location,
      description: event.description,
      classIds: event.classId ? [event.classId] : [],
    })
    fetchSchedule()
  } catch (err) {
    console.error('Failed to create event:', err)
  }
}

function onItemCreated() {
  fetchSchedule()
}

function handleAddDropdownClickOutside(event: MouseEvent) {
  if (addDropdownRef.value && !addDropdownRef.value.contains(event.target as Node)) {
    showAddDropdown.value = false
  }
}

// ── Watchers & lifecycle ──
watch([weekStart, monthStart, viewMode], () => {
  if (!settingsLoading.value) fetchSchedule()
})

onMounted(async () => {
  document.addEventListener('click', handleAddDropdownClickOutside)
  if (!scheduleSettings.isLoaded) {
    await scheduleSettings.fetchSettings()
  }
  settingsLoading.value = false
  fetchSchedule()
})
onUnmounted(() => document.removeEventListener('click', handleAddDropdownClickOutside))
</script>

<template>
  <div class="space-y-4">
    <!-- Header row -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <!-- Nav arrows -->
        <button class="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors" @click="previousPeriod">
          <font-awesome-icon :icon="['fas', 'chevron-left']" class="text-sm text-gray-500" />
        </button>
        <h1 class="text-lg font-bold text-gray-900 min-w-[180px] text-center">
          {{ viewMode === 'week' ? weekLabel : monthLabel }}
        </h1>
        <button class="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors" @click="nextPeriod">
          <font-awesome-icon :icon="['fas', 'chevron-right']" class="text-sm text-gray-500" />
        </button>
        <button class="px-3 py-1.5 text-sm font-medium text-bc-indigo-600 hover:bg-bc-indigo-50 rounded-lg transition-colors" @click="goToToday">
          Today
        </button>
      </div>
      <div class="flex items-center gap-2">
        <!-- Add dropdown (teachers only) -->
        <div v-if="auth.isTeacher" ref="addDropdownRef" class="relative">
          <button
            class="inline-flex items-center gap-2 px-3.5 py-2 bg-bc-indigo-500 text-white text-sm font-semibold rounded-lg hover:bg-bc-indigo-600 transition-colors"
            @click.stop="showAddDropdown = !showAddDropdown"
          >
            <font-awesome-icon :icon="['fas', 'plus']" class="text-xs" />
            Add
            <font-awesome-icon :icon="['fas', 'chevron-down']" class="text-[10px] opacity-70" />
          </button>
          <Transition enter-active-class="transition ease-out duration-100" enter-from-class="opacity-0 scale-95" enter-to-class="opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="opacity-100 scale-100" leave-to-class="opacity-0 scale-95">
            <div v-if="showAddDropdown" class="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-20">
              <button class="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left" @click="openAddSingle()">
                <div class="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <font-awesome-icon :icon="['fas', 'chalkboard-user']" class="text-blue-600 text-sm" />
                </div>
                <div>
                  <p class="font-medium text-gray-900 text-sm">Add Lesson</p>
                  <p class="text-xs text-gray-500">Single lesson</p>
                </div>
              </button>
              <button class="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left" @click="openAddSchedule()">
                <div class="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                  <font-awesome-icon :icon="['fas', 'calendar-week']" class="text-purple-600 text-sm" />
                </div>
                <div>
                  <p class="font-medium text-gray-900 text-sm">Schedule Lessons</p>
                  <p class="text-xs text-gray-500">Recurring weekly</p>
                </div>
              </button>
              <button class="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left" @click="openAddEvent()">
                <div class="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                  <font-awesome-icon :icon="['fas', 'calendar-day']" class="text-purple-600 text-sm" />
                </div>
                <div>
                  <p class="font-medium text-gray-900 text-sm">Add Event</p>
                  <p class="text-xs text-gray-500">Assemblies, trips & more</p>
                </div>
              </button>
            </div>
          </Transition>
        </div>
        <!-- View toggle -->
        <div class="bg-gray-100 rounded-lg p-0.5 flex">
          <button
            class="px-3 py-1.5 text-sm font-medium rounded-md transition-all inline-flex items-center gap-1.5"
            :class="viewMode === 'week' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
            @click="viewMode = 'week'"
          >
            <font-awesome-icon :icon="['fas', 'calendar-week']" class="text-xs" />
            Week
          </button>
          <button
            class="px-3 py-1.5 text-sm font-medium rounded-md transition-all inline-flex items-center gap-1.5"
            :class="viewMode === 'month' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
            @click="viewMode = 'month'"
          >
            <font-awesome-icon :icon="['fas', 'calendar']" class="text-xs" />
            Month
          </button>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" key="loading" class="flex items-center justify-center py-20">
      <font-awesome-icon :icon="['fas', 'spinner']" class="animate-spin text-2xl text-gray-300" />
    </div>

    <!-- ===== WEEK VIEW ===== -->
    <AppCard v-else-if="viewMode === 'week'" key="week" variant="widget" class="overflow-hidden">
      <WeekStrip
        :days="weekDays"
        :items-by-date="itemsByDate"
        :max-items="5"
        :show-add-button="auth.isTeacher"
        @item-click="navigateToItem"
        @add="(date: Date) => openAddSingle(date)"
      />
    </AppCard>

    <!-- ===== MONTH VIEW ===== -->
    <div v-else key="month" class="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <!-- Calendar -->
      <AppCard variant="widget" class="lg:col-span-2 p-4">
        <!-- Weekday headers -->
        <div class="grid grid-cols-7 mb-1">
          <div v-for="day in scheduleSettings.weekdayHeaders" :key="day" class="text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider py-2">
            {{ day }}
          </div>
        </div>
        <!-- Calendar grid -->
        <div class="grid grid-cols-7 gap-0.5">
          <button
            v-for="day in calendarDays"
            :key="day.dateString"
            class="aspect-square p-1 rounded-lg transition-all relative flex flex-col items-center justify-start pt-1.5"
            :class="{
              'bg-bc-indigo-500 text-white': day.isSelected,
              'bg-bc-indigo-50': day.isToday && !day.isSelected,
              'bg-gray-50/70': !day.isSchoolDay && !day.isSelected && !day.isToday,
              'hover:bg-gray-50': !day.isSelected && day.isCurrentMonth,
              'text-gray-300': !day.isCurrentMonth,
              'text-gray-400': day.isCurrentMonth && !day.isSelected && !day.isSchoolDay,
              'text-gray-900': day.isCurrentMonth && !day.isSelected && day.isSchoolDay,
            }"
            @click="selectDay(day.date)"
          >
            <span class="text-sm font-medium">{{ day.date.getDate() }}</span>
            <!-- Dots -->
            <div v-if="itemsByDate.get(day.dateString)?.length" class="flex gap-0.5 mt-0.5">
              <div
                v-for="item in itemsByDate.get(day.dateString)!.slice(0, 3)"
                :key="item.id"
                class="w-1.5 h-1.5 rounded-full"
                :style="{ backgroundColor: day.isSelected ? 'rgba(255,255,255,0.7)' : item.type === 'assignment' ? '#f97316' : item.type === 'event' ? '#8b5cf6' : getColorStyle(item.classColour).hex }"
              />
              <div
                v-if="(itemsByDate.get(day.dateString)?.length || 0) > 3"
                class="w-1.5 h-1.5 rounded-full"
                :style="{ backgroundColor: day.isSelected ? 'rgba(255,255,255,0.4)' : '#d1d5db' }"
              />
            </div>
          </button>
        </div>
      </AppCard>

      <!-- Agenda sidebar -->
      <AppCard variant="widget" class="overflow-hidden flex flex-col">
        <!-- Day header -->
        <div class="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm"
              :class="isSameDay(selectedDate, new Date()) ? 'bg-bc-indigo-500 text-white' : 'bg-gray-100 text-gray-700'"
            >
              {{ selectedDate.getDate() }}
            </div>
            <div>
              <p class="text-sm font-semibold text-gray-900">
                {{ selectedDate.toLocaleDateString('en-US', { weekday: 'long' }) }}
                <span v-if="isSameDay(selectedDate, new Date())" class="ml-1.5 px-1.5 py-0.5 bg-bc-indigo-100 text-bc-indigo-700 text-[10px] font-bold rounded-full uppercase">Today</span>
              </p>
              <p class="text-xs text-gray-400">{{ selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) }}</p>
            </div>
          </div>
          <button
            v-if="auth.isTeacher"
            class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-300 hover:text-bc-indigo-500 hover:bg-bc-indigo-50 transition-colors"
            title="Add lesson"
            @click="openAddSingle(selectedDate)"
          >
            <font-awesome-icon :icon="['fas', 'plus']" class="text-xs" />
          </button>
        </div>

        <!-- Items -->
        <div class="flex-1 overflow-y-auto p-4" style="max-height: 500px">
          <div v-if="selectedDayItems.length === 0" class="text-center py-10">
            <button
              v-if="auth.isTeacher"
              class="w-10 h-10 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center mx-auto mb-2 text-gray-300 hover:text-bc-indigo-500 hover:border-bc-indigo-300 hover:bg-bc-indigo-50/50 transition-all"
              @click="openAddSingle(selectedDate)"
            >
              <font-awesome-icon :icon="['fas', 'plus']" class="text-xs" />
            </button>
            <p class="text-xs text-gray-400">Nothing scheduled</p>
          </div>
          <div v-else class="space-y-2">
            <template v-for="item in selectedDayItems" :key="item.id">
              <ScheduleItemView
                v-if="fromScheduleItem(item)"
                :display="fromScheduleItem(item)!"
                variant="full"
                @click="navigateToItem(item)"
              />
            </template>

            <!-- Add button at bottom of list (teachers only) -->
            <button
              v-if="auth.isTeacher"
              class="w-full py-2 rounded-lg border border-dashed border-transparent hover:border-gray-200 text-gray-300 hover:text-bc-indigo-500 transition-all flex items-center justify-center gap-1"
              @click="openAddSingle(selectedDate)"
            >
              <font-awesome-icon :icon="['fas', 'plus']" class="text-[9px]" />
            </button>
          </div>
        </div>
      </AppCard>
    </div>

    <!-- Create single lesson modal -->
    <CreateLessonModal v-model="showCreateLesson" @created="onItemCreated" />

    <!-- Schedule recurring lessons modal -->
    <ScheduleLessonsModal v-model="showScheduleLessons" @created="onItemCreated" />

    <!-- Create event modal -->
    <CreateEventModal v-model="showCreateEvent" @created="onEventCreated" />
  </div>
</template>
